import { useCallback, useState } from 'react'
import useSWR from 'swr'
import {
  questions,
  EnneagramQuestion,
} from '@crystal-eyes/lib/assessments/enneagram'
import useAssessments from '@crystal-eyes/hooks/profiles/personality/useAssessments'
import logger from '@crystal-eyes/utils/logger'
import { fetchV3ApiAuthed } from '@crystal-eyes/utils/apis/v3Api'
import useAuth from '@crystal-eyes/hooks/useAuth'

interface Identity {
  id: string
}

type Options = {
  identity?: Identity
}

type State = {
  data: {
    currentQuestion: EnneagramQuestion
    currentAnswer?: number
    currentQuestionIndex: number
    more: boolean
    progress: number
    answerQuestion: (question: EnneagramQuestion, answer: number) => void
    goToQuestion: (index: number) => void
    submit: () => Promise<void>
    submitting: boolean
  }
}

let sharedQuestionIdx: number = 0
let sharedAssessmentAnswers: { [key: number]: number } = {}
export default function useEnneagram({ identity }: Options): State {
  const [submitting, setSubmitting] = useState<boolean>(false)
  const { refresh } = useAssessments({ identity })
  const { data: auth } = useAuth()

  const { data: currentQuestionIdx, mutate: mutateQuestionIdx } = useSWR(
    'useEnneagram.currentQuestionIdx',
    () => sharedQuestionIdx,
    {
      keepPreviousData: true,
      revalidateOnFocus: false,
    },
  )

  const { data: currentAnswers, mutate: mutateAnswers } = useSWR(
    'useEnneagram.currentAnswers',
    () => sharedAssessmentAnswers,
  )

  const goToQuestion = useCallback(
    (questionIdx: number) => {
      sharedQuestionIdx = Math.min(
        questions.length - 1,
        Math.max(0, questionIdx),
      )
      mutateQuestionIdx(sharedQuestionIdx)
    },
    [mutateQuestionIdx],
  )

  const answerQuestion = useCallback(
    (currentQuestion: EnneagramQuestion, rating: number) => {
      const questionIdx = questions.findIndex(
        q => q.question === currentQuestion.question,
      )

      logger.info(
        'Enneagram - Answered Question',
        currentQuestion,
        `(${rating})`,
      )

      mutateAnswers(curr => {
        const newValue = { ...(curr || {}), [questionIdx]: rating }
        sharedAssessmentAnswers = newValue
        return newValue
      })

      goToQuestion((currentQuestionIdx || 0) + 1)
    },
    [currentQuestionIdx, goToQuestion, mutateAnswers],
  )

  const currentAnswer = currentAnswers?.[currentQuestionIdx || 0]
  const answeredQuestions = Object.keys(currentAnswers || {}).length
  const progress = (answeredQuestions / questions.length) * 100.0

  const submit = useCallback(() => {
    setSubmitting(true)
    const enneagramType = getEnneagramType(currentAnswers || {})
    logger.info('Submitting Enneagram Assessment', enneagramType)

    return fetchV3ApiAuthed(auth, 'enneagram', {
      method: 'POST',
      body: JSON.stringify({
        data: getResponses(currentAnswers || {}),
        text_type: enneagramType,
      }),
    })
      .then(() => {
        return refresh()
      })
      .finally(() => setSubmitting(false))
  }, [auth, currentAnswers, refresh, setSubmitting])

  return {
    data: {
      currentQuestion: questions[currentQuestionIdx || 0],
      currentQuestionIndex: currentQuestionIdx || 0,
      more: !currentQuestionIdx || currentQuestionIdx < questions.length - 1,
      currentAnswer,
      answerQuestion,
      progress,
      goToQuestion,
      submit,
      submitting,
    },
  }
}

type EnneagramAnswers = { [key: string]: number }

function getEnneagramType(currentAnswers: { [key: number]: number }): string {
  const answers: EnneagramAnswers = Object.entries(currentAnswers).reduce(
    (
      acc: { [key: string]: number },
      [questionIdx, rating]: [string, number],
    ) => {
      return {
        ...acc,
        [questionIdx]: rating,
      }
    },
    {},
  )

  return getType(answers)
}

export function getType(answers: EnneagramAnswers): string {
  const scores = getTypeScores(answers)
  let type = ''
  let maxScore = 0

  for (const currType in scores) {
    if (scores[currType] >= maxScore) {
      maxScore = scores[currType]
      type = currType
    }
  }

  return type
}

function getScore(answers: Record<string, number>, answerKeys: Array<number>) {
  return answerKeys
    .map(key => answers[String(key)])
    .reduce((total, curr) => total + curr, 0)
}

function getResponses(currentAnswers: { [key: number]: number }): {
  [key: string]: any
} {
  const answers = Object.entries(currentAnswers).map(
    ([questionIdx, rating]: [string, number]) => {
      const question = questions[parseInt(questionIdx)]

      return { ...question, rating }
    },
  )

  const formattedAnswers: EnneagramAnswers = Object.entries(
    currentAnswers,
  ).reduce(
    (
      acc: { [key: string]: number },
      [questionIdx, rating]: [string, number],
    ) => {
      return {
        ...acc,
        [questionIdx]: rating,
      }
    },
    {},
  )

  return { answers, type_scores: getTypeScores(formattedAnswers) }
}

export function getTypeScores(answers: EnneagramAnswers): EnneagramAnswers {
  const typeScores = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
    6: 0,
    7: 0,
    8: 0,
    9: 0,
  }

  typeScores[1] = getScore(answers, [0, 9, 18, 27])
  typeScores[2] = getScore(answers, [1, 10, 19, 28])
  typeScores[3] = getScore(answers, [2, 11, 20, 29])
  typeScores[4] = getScore(answers, [3, 12, 21, 30])
  typeScores[5] = getScore(answers, [4, 13, 22, 31])
  typeScores[6] = getScore(answers, [5, 14, 23, 32])
  typeScores[7] = getScore(answers, [6, 15, 24, 33])
  typeScores[8] = getScore(answers, [7, 16, 25, 34])
  typeScores[9] = getScore(answers, [8, 17, 26, 35])

  return typeScores
}
